import { useState, useLayoutEffect, useCallback } from 'react';

/**
 * useFloatingPosition — positionne un élément flottant (menu, popover, tooltip…)
 * en `position: fixed` relativement au rect du trigger.
 *
 * Stratégie :
 *   - Initial state : top/left à -9999 (off-screen) pour ne pas montrer le
 *     menu en haut-gauche pendant le premier paint avant qu'on ait mesuré.
 *   - On ne touche PAS à `opacity` ni à `visibility` ici : on laisse les
 *     animations CSS (`animate-scale-in`, `animate-fade-in`) gérer ça.
 *   - Le Portal qui héberge l'élément doit être synchrone, sinon la ref
 *     est null quand useLayoutEffect s'exécute et le menu reste à -9999.
 *
 * Recalcule la position sur scroll (capture phase) et resize.
 */
const VIEWPORT_PADDING = 8;
const OFFSCREEN = { position: 'fixed', top: -9999, left: -9999 };

export function useFloatingPosition({
  triggerRef,
  floatingRef,
  open,
  side = 'bottom',
  align = 'start',
  offset = 4,
  matchTriggerWidth = false,
}) {
  const [style, setStyle] = useState(OFFSCREEN);

  const update = useCallback(() => {
    if (!triggerRef.current || !floatingRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const floating = floatingRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = 0;
    let left = 0;

    if (side === 'bottom') top = trigger.bottom + offset;
    else if (side === 'top') top = trigger.top - floating.height - offset;
    else if (side === 'right') left = trigger.right + offset;
    else if (side === 'left') left = trigger.left - floating.width - offset;

    if (side === 'top' || side === 'bottom') {
      if (align === 'start') left = trigger.left;
      else if (align === 'end') left = trigger.right - floating.width;
      else left = trigger.left + (trigger.width - floating.width) / 2;
    } else {
      if (align === 'start') top = trigger.top;
      else if (align === 'end') top = trigger.bottom - floating.height;
      else top = trigger.top + (trigger.height - floating.height) / 2;
    }

    // Flip vertical si on déborde de l'autre côté
    if (side === 'bottom' && top + floating.height > vh - VIEWPORT_PADDING) {
      const flipped = trigger.top - floating.height - offset;
      if (flipped >= VIEWPORT_PADDING) top = flipped;
    } else if (side === 'top' && top < VIEWPORT_PADDING) {
      const flipped = trigger.bottom + offset;
      if (flipped + floating.height <= vh - VIEWPORT_PADDING) top = flipped;
    }

    if (left + floating.width > vw - VIEWPORT_PADDING) left = vw - floating.width - VIEWPORT_PADDING;
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    if (top + floating.height > vh - VIEWPORT_PADDING) top = vh - floating.height - VIEWPORT_PADDING;
    if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;

    const next = { position: 'fixed', top, left };
    if (matchTriggerWidth) next.width = trigger.width;
    setStyle(next);
  }, [triggerRef, floatingRef, side, align, offset, matchTriggerWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(OFFSCREEN);
      return undefined;
    }
    update();
    const handler = () => update();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, update]);

  return { style, update };
}

export default useFloatingPosition;
